import React from 'react'
import { Controller, Control, ControllerRenderProps } from 'react-hook-form'
import {
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps
} from '@mui/material'
import {
  SelectMultilineSkeleton,
  SelectSkeleton
} from '@components/common/Skeletons'
import { inputLabelClasses } from '@mui/material/InputLabel'

interface TextFieldItemControllerProps extends Omit<TextFieldProps, 'name'> {
  errorMessage?: string
  fullWidth?: boolean
  control: Control<any>
  name: string
  defaultValue?: any
  label?: string
}

export const TextFieldItemController: React.FC<
  TextFieldItemControllerProps
> = ({
  errorMessage,
  fullWidth = true,
  control,
  name,
  defaultValue,
  label,
  ...props
}) => {
  const isError = Boolean(errorMessage)

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <FormControl fullWidth={fullWidth} margin="dense">
          <TextField
            variant="outlined"
            size="small"
            label={label}
            error={isError}
            helperText={isError && errorMessage}
            {...props}
            {...field}
          />
        </FormControl>
      )}
    />
  )
}

interface TextFieldItemProps extends Omit<TextFieldProps, 'field'> {
  field: ControllerRenderProps<any, any>
  errorMessage?: string
  data_testid?: string
  fullWidth?: boolean
  focused?: boolean
  required?: boolean
  endAdornmentContent?: React.ReactNode
  endAdornmentAction?: string
  endAdornmentClick?: () => void
  selectOnFocus?: boolean
  loading?: boolean
  fieldState?: any
  formState?: any
}

const TextFieldItem: React.FC<TextFieldItemProps> = ({
  field,
  errorMessage,
  data_testid,
  fullWidth = true,
  focused = false,
  required = false,
  endAdornmentContent = null,
  endAdornmentAction = '',
  endAdornmentClick = () => null,
  selectOnFocus = false,
  loading = false,
  ...props
}) => {
  // Remove props that aren't needed for TextField
  delete props.fieldState
  delete props.formState

  const isError = Boolean(errorMessage)

  return (
    <>
      {!loading && (
        <FormControl fullWidth={fullWidth} margin="dense">
          <TextField
            data-testid={data_testid}
            autoFocus={focused}
            required={required}
            error={isError}
            helperText={
              isError && (
                <span style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</span>
              )
            }
            InputProps={{
              ...(endAdornmentContent && {
                endAdornment: (
                  <InputAdornment position="end">
                    {endAdornmentAction ? (
                      <IconButton
                        edge="end"
                        type={
                          endAdornmentAction as
                            | 'submit'
                            | 'reset'
                            | 'button'
                            | undefined
                        }
                        onClick={endAdornmentClick}
                        data-testid="IconButton-70fa92cd-9ddd-4806-8706-1a8b4b2eeda0"
                      >
                        {endAdornmentContent}
                      </IconButton>
                    ) : (
                      endAdornmentContent
                    )}
                  </InputAdornment>
                )
              })
            }}
            InputLabelProps={{
              sx: {
                [`&.${inputLabelClasses.shrink}`]: {
                  color: '#7966eb!important'
                }
              }
            }}
            onFocus={event => (selectOnFocus ? event.target.select() : null)}
            {...field}
            {...props}
            maxRows={3}
          />
        </FormControl>
      )}

      {loading && props.multiline && <SelectMultilineSkeleton />}
      {loading && !props.multiline && <SelectSkeleton />}
    </>
  )
}

export default TextFieldItem
